// src/features/activity/InboxTab.tsx
// The inbox: unread activities grouped by the entity they relate to.
// ListInbox, AcknowledgeActivities, AcknowledgeAll.

import { useCallback, useEffect, useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { activityInboxStore } from '@core/stores';
import { Button, Badge, ErrorAlert } from '@react/components';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ListInbox } from '@core/api/endpoints/activity/list_inbox';
import type { Activity, InboxGroup } from '@core/types/activity';
import { INBOX_FILTERS, type InboxFilterKey, relativeTime } from './logic';

interface InboxTabProps {
  onOpenActivity: (activity: Activity) => void;
  selectedId?: string;
  onAcknowledged: () => void;
}

export const InboxTab = observer(({ onOpenActivity, selectedId, onAcknowledged }: InboxTabProps) => {
  const { t } = useI18n();
  const [filter, setFilter] = useState<InboxFilterKey>('all');
  const [groups, setGroups] = useState<InboxGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [markAllOpen, setMarkAllOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    ListInbox({ filter: filter === 'all' ? undefined : filter, limit: 50 })
      .then((res) => setGroups(res.groups ?? []))
      .catch(() => setError(t('activity.inbox.loadError')))
      .finally(() => setLoading(false));
  }, [filter, t]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const acknowledgeIds = async (ids: string[]) => {
    if (ids.length === 0) return;
    setBusyId(ids[0]);
    try {
      await activityInboxStore.AcknowledgeActivities({ activity_ids: ids });
      load();
      onAcknowledged();
    } catch {
      setError(t('activity.inbox.loadError'));
    } finally {
      setBusyId(null);
    }
  };

  const markAllRead = async () => {
    setMarkAllOpen(false);
    try {
      await activityInboxStore.AcknowledgeAll({
        filter: { categories: [], verbs: [], object_types: [], inbox_filter: filter === 'all' ? undefined : filter },
      });
      load();
      onAcknowledged();
    } catch {
      setError(t('activity.inbox.loadError'));
    }
  };

  const totalUnread = groups.reduce((sum, g) => sum + (g.unread_count ?? 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5" role="tablist" aria-label={t('activity.inbox.filter.all')}>
          {INBOX_FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              role="tab"
              aria-selected={filter === f}
              onClick={() => setFilter(f)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                filter === f ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:text-foreground'
              }`}
            >
              {t(`activity.inbox.filter.${f}`)}
            </button>
          ))}
        </div>
        <Button size="sm" variant="secondary" onClick={() => setMarkAllOpen(true)} disabled={totalUnread === 0}>
          {t('activity.inbox.markAllRead')}
        </Button>
      </div>

      {error && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}

      {loading && groups.length === 0 && (
        <div className="h-32 animate-pulse rounded-md bg-muted" aria-busy="true" />
      )}

      {!loading && groups.length === 0 && !error && (
        <div className="rounded-md border border-dashed border-border p-10 text-center">
          <p className="text-sm font-medium text-foreground">{t('activity.inbox.empty.title')}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t('activity.inbox.empty.description')}</p>
        </div>
      )}

      <ul className="space-y-2">
        {groups.map((group) => {
          const gid = group.object?.id ?? group.object_title;
          const isOpen = expanded.has(gid);
          const unreadIds = group.items
            .filter((it) => it.state !== 'ACKNOWLEDGMENT_STATE_ACKNOWLEDGED')
            .map((it) => it.activity?.entity?.id)
            .filter((v): v is string => !!v);

          return (
            <li key={gid} className="rounded-md border border-border">
              <div className="flex items-center justify-between gap-2 p-3">
                <button type="button" onClick={() => toggle(gid)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
                  <span className="truncate text-sm font-medium text-foreground">{group.object_title || group.object?.type_name || gid}</span>
                  {group.unread_count > 0 && <Badge variant="primary" size="sm">{group.unread_count}</Badge>}
                  <span className="ml-auto shrink-0 text-xs text-muted-foreground">{relativeTime(group.latest_at as unknown as string)}</span>
                </button>
                {unreadIds.length > 0 && (
                  <Button size="sm" variant="ghost" isLoading={busyId === unreadIds[0]} onClick={() => acknowledgeIds(unreadIds)}>
                    {t('activity.inbox.ackGroup')}
                  </Button>
                )}
              </div>
              {isOpen && (
                <ul className="divide-y divide-border border-t border-border">
                  {group.items.map((item) => {
                    const id = item.activity?.entity?.id ?? '';
                    const acked = item.state === 'ACKNOWLEDGMENT_STATE_ACKNOWLEDGED';
                    return (
                      <li key={id} className={`flex items-center justify-between gap-2 px-3 py-2 ${selectedId === id ? 'bg-muted/50' : ''}`}>
                        <button
                          type="button"
                          onClick={() => item.activity && onOpenActivity(item.activity)}
                          className={`min-w-0 flex-1 truncate text-left text-sm ${acked ? 'text-muted-foreground' : 'text-foreground'}`}
                        >
                          {item.activity?.summary}
                        </button>
                        {!acked && (
                          <Button size="sm" variant="ghost" isLoading={busyId === id} onClick={() => acknowledgeIds([id])}>
                            {t('activity.inbox.ack')}
                          </Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>

      <ConfirmDialog
        open={markAllOpen}
        title={t('activity.inbox.markAllRead')}
        description={t('activity.inbox.markAllReadConfirm')}
        onConfirm={markAllRead}
        onCancel={() => setMarkAllOpen(false)}
      />
    </div>
  );
});

export default InboxTab;
